import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // PLW: own the <details> open attribute in JS. The summary's native toggle
    // removes `open` the instant a closing click lands, which unrenders the
    // panel mid-transition; the frozen transition keeps #close's onAnimationEnd
    // promise pending, and its reset() then fires on the NEXT open and yanks
    // the drawer straight back shut ("hamburger stops working"). Preventing the
    // default lets the close animation finish while the panel is still rendered
    // (open() sets the attribute itself, reset() removes it after the slide).
    event?.preventDefault();
    // menu-open (not [open]) is the VISUAL state: during the close animation
    // the open attribute is still set while the panel slides out — a toggle at
    // that moment should reopen, not re-close.
    return this.refs.details.classList.contains('menu-open') ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    // PLW: the summary click's default action is prevented (see toggle) — set
    // the attribute here so the details content renders before animating open.
    details.setAttribute('open', '');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      // PLW: the custom nav panel is `.plw-nav-drawer__panel` (it can't use the
    // `.menu-drawer` class — that drags in Horizon's drawer width/styles). Include
    // it so the open/close waits for the real slide animation, then resets the
    // <details open> (which releases the scroll-lock). Without it the query
    // returns null, falls back to the never-animating <details>, and the reset
    // (and scroll-lock release) is left to fire on a best-effort microtask.
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu, .plw-nav-drawer__panel');
      onAnimationEnd(
        drawer || details,
        () => {
          // PLW: stale-callback guard — skip if the drawer closed again before
          // the open animation settled.
          if (details.classList.contains('menu-open')) trapFocus(details);
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    // PLW: the custom nav panel is `.plw-nav-drawer__panel` (it can't use the
    // `.menu-drawer` class — that drags in Horizon's drawer width/styles). Include
    // it so the open/close waits for the real slide animation, then resets the
    // <details open> (which releases the scroll-lock). Without it the query
    // returns null, falls back to the never-animating <details>, and the reset
    // (and scroll-lock release) is left to fire on a best-effort microtask.
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu, .plw-nav-drawer__panel');

    onAnimationEnd(
      drawer || details,
      () => {
        // PLW: stale-callback guard — if the drawer was reopened before this
        // close's animations settled (or a frozen transition released them
        // late), don't reset the now-open drawer.
        if (details.classList.contains('menu-open')) return;
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
